"use client"

import { useEffect, useRef } from "react"
import { gsap } from "gsap"
import { Link } from "react-router-dom"
import HomeNavbar from "./HomeNavbar"
import Footer from "./Footer"

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-slate-900 text-white overflow-hidden">
      <HomeNavbar />
      <main className="flex-grow">
        <HeroSection />
        <AboutUsSection />
        <OurMissionSection />
        <OurValuesSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

const HeroSection = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const heroChildren = heroRef.current?.children || [];
    gsap.set(heroChildren, { y: 50, opacity: 0 });
    gsap.to(heroChildren, {
      y: 0,
      opacity: 1,
      duration: 1,
      stagger: 0.2,
      ease: "power3.out",
      delay: 0.5,
    });
  }, []);

  return (
    <section className="relative z-10 py-24 px-4 md:px-6 lg:px-8 bg-slate-800/50 backdrop-blur-sm">
      <div ref={heroRef} className="max-w-4xl mx-auto text-center">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
          About AlgoSoftware
        </h1>
        <p className="text-xl text-slate-300 mb-8">
          Learn more about our mission, vision, and the values that drive us forward.
        </p>
      </div>
    </section>
  );
};

const AboutUsSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const items = sectionRef.current?.querySelectorAll('.about-item') || [];
    gsap.set(items, { x: -50, opacity: 0 });
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(entry.target, {
              x: 0,
              opacity: 1,
              duration: 0.8,
              ease: "power3.out",
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    items.forEach((item) => observer.observe(item));

    return () => items.forEach((item) => observer.unobserve(item));
  }, []);

  return (
    <section ref={sectionRef} className="relative z-10 py-20 px-4 md:px-6 lg:px-8 bg-slate-800/50 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div className="about-item">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Who We Are</h2>
          <p className="text-slate-300 mb-6">
            AlgoSoftware is a modern Algo Trading platform that empowers both beginner and experienced traders to achieve consistent growth.
            We believe that by following successful traders, anyone can become a more effective trader.
          </p>
          <p className="text-slate-300">
            Our platform provides access to a wide network of expert traders and offers seamless copying of their trades in real-time.
          </p>
        </div>
        <div className="about-item">
          <img
            src="images/about-us-image.jpg"
            alt="About AlgoSoftware"
            className="w-full rounded-lg shadow-lg"
          />
        </div>
      </div>
    </section>
  );
};

const OurMissionSection = () => {
  const sectionRef = useRef(null);
  const missionSteps = [
    { title: 'Empowerment', description: 'Empowering traders to achieve financial independence.' },
    { title: 'Transparency', description: 'Providing a transparent platform with full visibility of trades.' },
    { title: 'Innovation', description: 'Building an innovative system with cutting-edge technology.' },
  ];

  useEffect(() => {
    const cards = sectionRef.current?.querySelectorAll('.mission-card') || [];
    gsap.set(cards, { y: 50, opacity: 0 });
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(entry.target, {
              y: 0,
              opacity: 1,
              duration: 0.8,
              ease: "power3.out",
              delay: 0.1 * (Array.from(cards).indexOf(entry.target)),
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    cards.forEach((card) => observer.observe(card));

    return () => cards.forEach((card) => observer.unobserve(card));
  }, []);

  return (
    <section ref={sectionRef} className="relative z-10 py-20 px-4 md:px-6 lg:px-8 bg-slate-800/50 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Our Mission</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {missionSteps.map((step, index) => (
            <div
              key={index}
              className="mission-card bg-slate-700/50 backdrop-blur-sm p-8 rounded-lg hover:bg-slate-700/80 transition-colors"
            >
              <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
              <p className="text-slate-300">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const OurValuesSection = () => {
  const sectionRef = useRef(null);
  const values = [
    { title: 'Trust', description: 'We value trust and integrity in every aspect of our platform.' },
    { title: 'Innovation', description: 'We continuously seek to innovate and improve our technology.' },
    { title: 'Community', description: 'We build a strong community of traders, mentors, and learners.' },
  ];

  useEffect(() => {
    const cards = sectionRef.current?.querySelectorAll('.value-card') || [];
    gsap.set(cards, { scale: 0.9, opacity: 0 });
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(entry.target, {
              scale: 1,
              opacity: 1,
              duration: 0.8,
              ease: "power3.out",
              delay: 0.1 * (Array.from(cards).indexOf(entry.target)),
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    cards.forEach((card) => observer.observe(card));

    return () => cards.forEach((card) => observer.unobserve(card));
  }, []);

  return (
    <section ref={sectionRef} className="relative z-10 py-20 px-4 md:px-6 lg:px-8 bg-slate-800/50 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Our Values</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {values.map((value, index) => (
            <div
              key={index}
              className="value-card bg-slate-700/50 backdrop-blur-sm p-8 rounded-lg hover:bg-slate-700/80 transition-colors text-center"
            >
              <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
              <p className="text-slate-300">{value.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const ctaRef = useRef(null);

  useEffect(() => {
    const ctaElement = ctaRef.current; // Capture the ref value
    const ctaChildren = ctaElement?.children || [];
    gsap.set(ctaChildren, { y: 50, opacity: 0 });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(ctaChildren, {
              y: 0,
              opacity: 1,
              duration: 0.8,
              stagger: 0.2,
              ease: "power3.out",
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    if (ctaElement) observer.observe(ctaElement);

    return () => {
      if (ctaElement) observer.unobserve(ctaElement); // Use captured value in cleanup
    };
  }, []); // Empty dependency array since this runs once on mount

  return (
    <section className="relative z-10 py-20 px-4 md:px-6 lg:px-8 bg-blue-600">
      <div ref={ctaRef} className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">Join the Algo Trading Revolution</h2>
        <p className="text-xl text-slate-200 mb-8">
          Become part of a growing community of traders and start your journey to financial freedom.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center justify-center h-12 px-8 rounded-md font-medium bg-white text-blue-600 hover:bg-blue-700 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          Get Started Now
        </Link>
      </div>
    </section>
  );
};
export default AboutPage;